import React, { useState, useEffect } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Maximize, Minimize } from 'lucide-react';
import { useApp } from '../contexts/AppContext';

const ZOOM_LEVELS = [70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120, 125, 130, 140, 150];

export default function ZoomControl() {
  const { lang } = useApp();
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem('shiksha-zoom');
    return saved ? parseInt(saved) : 100;
  });
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    document.documentElement.style.fontSize = `${zoom}%`;
    localStorage.setItem('shiksha-zoom', zoom.toString());
  }, [zoom]);

  const decrease = () => {
    const idx = ZOOM_LEVELS.indexOf(zoom);
    if (idx > 0) setZoom(ZOOM_LEVELS[idx - 1]);
    else if (zoom > ZOOM_LEVELS[0]) setZoom(ZOOM_LEVELS.reduce((prev, curr) => curr < zoom ? curr : prev, ZOOM_LEVELS[0]));
  };

  const increase = () => {
    const idx = ZOOM_LEVELS.indexOf(zoom);
    if (idx < ZOOM_LEVELS.length - 1) setZoom(ZOOM_LEVELS[idx + 1]);
    else if (zoom < ZOOM_LEVELS[ZOOM_LEVELS.length - 1]) setZoom(ZOOM_LEVELS.find(l => l > zoom) || ZOOM_LEVELS[ZOOM_LEVELS.length - 1]);
  };

  const reset = () => setZoom(100);

  return (
    <div data-testid="zoom-control" style={{
      position: 'fixed', bottom: 12, left: '50%', transform: 'translateX(-50%)',
      zIndex: 90, display: 'flex', alignItems: 'center', gap: 4,
      background: 'var(--color-surface-1)', borderRadius: 'var(--radius-pill)',
      boxShadow: 'var(--shadow-lg)', border: '1px solid var(--color-separator)',
      padding: isExpanded ? '6px 12px' : '6px 10px',
      transition: 'all var(--duration-base) var(--ease-out)',
    }}>
      {isExpanded ? (
        <>
          {/* Decrease */}
          <button data-testid="zoom-decrease" onClick={decrease} disabled={zoom <= ZOOM_LEVELS[0]}
            style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: zoom <= ZOOM_LEVELS[0] ? 'var(--color-surface-2)' : 'var(--color-accent-light)',
              color: zoom <= ZOOM_LEVELS[0] ? 'var(--color-label-quarternary)' : 'var(--color-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ZoomOut size={16} />
          </button>

          {/* Zoom slider */}
          <input type="range" min={ZOOM_LEVELS[0]} max={ZOOM_LEVELS[ZOOM_LEVELS.length - 1]} value={zoom}
            onChange={e => setZoom(parseInt(e.target.value))}
            style={{ width: 120, height: 4, accentColor: 'var(--color-accent)', cursor: 'pointer' }} />

          {/* Zoom label */}
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-label-primary)', minWidth: 36, textAlign: 'center' }}>
            {zoom}%
          </span>

          {/* Increase */}
          <button data-testid="zoom-increase" onClick={increase} disabled={zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
            style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1] ? 'var(--color-surface-2)' : 'var(--color-accent-light)',
              color: zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1] ? 'var(--color-label-quarternary)' : 'var(--color-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ZoomIn size={16} />
          </button>

          {/* Reset */}
          <button data-testid="zoom-reset" onClick={reset}
            style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: zoom === 100 ? 'var(--color-surface-2)' : 'var(--color-warning-bg)',
              color: zoom === 100 ? 'var(--color-label-quarternary)' : 'var(--color-warning)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title={lang === 'bn' ? 'রিসেট (১০০%)' : 'Reset (100%)'}>
            <RotateCcw size={14} />
          </button>

          {/* Collapse */}
          <button onClick={() => setIsExpanded(false)}
            style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: 'transparent', color: 'var(--color-label-tertiary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Minimize size={12} />
          </button>
        </>
      ) : (
        <button onClick={() => setIsExpanded(true)} data-testid="zoom-toggle"
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
            cursor: 'pointer', color: 'var(--color-label-secondary)', fontSize: 12, fontWeight: 500, padding: '2px 4px' }}>
          <Maximize size={14} />
          <span>{zoom}%</span>
        </button>
      )}
    </div>
  );
}
